import {
  Button,
  confirm,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import { Edit, Eye, History, RotateCcw } from "lucide-react";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { useDisclosure } from "@/shared/hooks/useDisclosure";
import { formatDateTime } from "@/shared/lib/date-time";
import {
  useJDVersion,
  useJDVersions,
  useRevertJDVersion,
} from "../api/queries";
import { JDDetailView } from "./JDDetailView";

type JDVersionHistoryProps = {
  jdId: string;
};

export function JDVersionHistory({ jdId }: JDVersionHistoryProps) {
  const { data, isLoading } = useJDVersions(jdId);
  const revert = useRevertJDVersion();
  const viewVersionModal = useDisclosure<string>();
  const nav = useNavigate();
  const canEdit = useActionPermission(["jd:update"]);

  const selectedVersion = useJDVersion(jdId, viewVersionModal.data || "");

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <p className="text-muted-foreground">ກຳລັງໂຫຼດ...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <History className="h-5 w-5" />
          <h3 className="font-semibold text-lg">ປະຫວັດເວີຊັນ</h3>
        </div>
        {/* <Button
          variant="outline"
          size="sm"
          onClick={() => {
            saveVersion.mutate({ id: jdId, changeNote: "Manual version save" });
          }}
          disabled={saveVersion.isPending}
        >
          <Save className="mr-1 h-4 w-4" />
          ບັນທຶກເວີຊັນ
        </Button> */}
      </div>

      {!data?.data || data.data.length === 0 ? (
        <div className="py-8 text-center text-muted-foreground">
          <p>ຍັງບໍ່ມີເວີຊັນ</p>
          <p className="mt-2 text-xs">ເວີຊັນຈະຖືກບັນທຶກອັດຕະໂນມັດເມື່ອມີການແກ້ໄຂ</p>
        </div>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[100px]">ເວີຊັນ</TableHead>
                <TableHead className="w-[150px]">ແກ້ໄຂໂດຍ</TableHead>
                <TableHead className="w-[180px]">ວັນທີ</TableHead>
                <TableHead className="w-[200px]">ໝາຍເຫດການປ່ຽນແປງ</TableHead>
                <TableHead className="w-[200px]">ການກະທໍາ</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.data.map((version) => (
                <TableRow key={version.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">
                        v{version.versionNumber}
                      </span>
                      {version.isCurrent && (
                        <span className="rounded bg-primary/10 px-2 py-0.5 text-primary text-xs">
                          ປັດຈຸບັນ
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">
                      {version.editedByUser?.name || version.editedBy}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">
                      {formatDateTime(version.editedAt)}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="text-muted-foreground text-sm">
                      {version.changeNote || "-"}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => viewVersionModal.openWith(version.id)}
                      >
                        <Eye className="mr-1 h-4 w-4" />
                        ເບິ່ງ
                      </Button>
                      {canEdit && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            nav({
                              to: `/app/jd/${jdId}/edit`,
                              search: { versionId: version.id },
                            });
                          }}
                        >
                          <Edit className="mr-1 h-4 w-4" />
                          ແກ້ໄຂ
                        </Button>
                      )}
                      {!version.isCurrent && canEdit && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            const confirmed = await confirm({
                              title: "ກັບຄືນເວີຊັນ",
                              description: `ທ່ານແນ່ໃຈບໍ່ວ່າຕ້ອງການກັບຄືນໄປເວີຊັນ ${version.versionNumber}?`,
                            });
                            if (confirmed) {
                              revert.mutate({
                                id: jdId,
                                versionId: version.id,
                              });
                            }
                          }}
                          disabled={revert.isPending}
                        >
                          <RotateCcw className="mr-1 h-4 w-4" />
                          ກັບຄືນ
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Version Detail Modal */}
      <Modal
        open={viewVersionModal.isOpen}
        onOpenChange={(open) => {
          if (!open) viewVersionModal.close();
        }}
        title="ລາຍລະອຽດເວີຊັນ"
        size="lg"
      >
        <div className="max-h-[80vh] overflow-y-auto">
          {selectedVersion.isLoading ? (
            <div className="flex items-center justify-center py-8">
              <p className="text-muted-foreground">ກຳລັງໂຫຼດ...</p>
            </div>
          ) : selectedVersion.data ? (
            <JDDetailView jd={selectedVersion.data} hideVersionHistory />
          ) : (
            <div className="flex items-center justify-center py-8">
              <p className="text-muted-foreground">ບໍ່ພົບຂໍ້ມູນເວີຊັນ</p>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}
